import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, catchError, map } from 'rxjs';

/**
 * Which language the app's strings are in.
 *
 * The language is the signed-in user's culture, read at boot from the token and after sign-in from
 * the profile, and en-US for anyone signed out. Each culture is one ngx-translate language, loaded
 * once by CatalogTranslateLoader; the server has already merged it over en-US, so no default
 * language is configured on the client.
 */
@Injectable({ providedIn: 'root' })
export class AppLanguageService {
  static readonly fallback = 'en-US';

  constructor(private translate: TranslateService) {}

  /**
   * Switches to `culture`, loading its strings first if needed. Never errors: if the strings cannot
   * be loaded, it switches to an empty set, so keys render as [KEY] placeholders through
   * CommonMissingTranslationHandler instead of the app (or an APP_INITIALIZER) stalling.
   */
  use(culture: string | null | undefined): Observable<void> {
    const lang = AppLanguageService.normalize(culture);
    return this.translate.use(lang).pipe(
      catchError((err) => {
        console.error(`Strings for ${lang} could not be loaded; showing untranslated keys.`, err);
        // A failed request stays cached in ngx-translate, so give it a translation it can switch to.
        this.translate.setTranslation(lang, {});
        return this.translate.use(lang);
      }),
      map(() => undefined),
    );
  }

  /**
   * `es-mx` and `es-MX` must not become two languages loaded twice. Anything that is not a
   * language-region code (`RatePlan`, empty) is served in en-US by the server, so ask for that.
   */
  static normalize(culture: string | null | undefined): string {
    const match = /^([a-z]{2})-([a-z]{2})$/i.exec(culture ?? '');
    return match ? `${match[1].toLowerCase()}-${match[2].toUpperCase()}` : AppLanguageService.fallback;
  }
}
